import { cloneDeep } from 'lodash'
import {
  SERVICE_TYPES,
  TECH_FEE_SERVICE_TYPES,
  NATIONAL_MX_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES,
  NATIONAL_BR_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES,
  CHANNEL_PRICES_DISCARD_CODE,
  CHANNEL_PRICE_BUSINESS_TYPES,
  CHANNELS_WITHOUT_BUSINESS_TYPE,
  PRICE_MODES,
  CONFIG_CUTOFF_INTERVALS,
  DEFAULT_MINIMUM_FEE_BY_CURRENCY,
} from './constants'

export const findOrCreateBillingService = ({
  serviceType,
  serviceTypeCategory = 'NONE_APPLIES',
  billingServices = null,
  currency = null,
  storeId,
  warehouseId,
}) => {
  // Shipping method prices share (type, zone) with the default row, so only default rows match.
  const foundBillingService = billingServices?.find(({ service_type, service_type_category, shipping_method_id }) => {
    return service_type === serviceType && service_type_category === serviceTypeCategory && !shipping_method_id
  })

  if (foundBillingService) {
    return foundBillingService
  }
  const createdBillingService = {
    store_id: storeId,
    warehouse_id: warehouseId,
    service_type: serviceType,
    service_type_category: serviceTypeCategory,
    currency: currency,
    price_configuration: [{ from: 0, to: null, price: null, price_mode: null, range_exclusion: null, excess: false }],
  }
  return createdBillingService
}

export const deleteBillingService = ({ serviceType, serviceTypeCategory = 'NONE_APPLIES', billingServices }) => {
  if (!billingServices) {
    return []
  }

  const updatedBillingServices = billingServices.filter(({ service_type, service_type_category, shipping_method_id }) => {
    return !(service_type === serviceType && service_type_category === serviceTypeCategory && !shipping_method_id)
  })

  return updatedBillingServices
}

// Writes a default row's prices; a shipping method price of the same zone is a different row.
export const updateDefaultPriceConfiguration = (billingServices, service, priceConfiguration) => {
  const isTarget = ({ service_type, service_type_category, shipping_method_id }) => {
    return service_type === service.service_type && service_type_category === service.service_type_category && !shipping_method_id
  }
  const updated = cloneDeep(billingServices)
  const target = updated.find(isTarget)

  if (target) {
    target.price_configuration = priceConfiguration
    return updated
  }
  return [...updated, { ...cloneDeep(service), price_configuration: priceConfiguration }]
}

const EMPTY_PRICE_CONFIGURATION = [{ from: 0, to: null, price: null, price_mode: null, range_exclusion: null, excess: false }]

// The zone price and its extended zone surcharge can both have a shipping method version.
export const SHIPPING_METHOD_PRICE_SERVICE_TYPES = [SERVICE_TYPES.SERVICE_SHIPPING_LABEL, SERVICE_TYPES.SERVICE_SHIPPING_LABEL_EXTENDED_ZONE]

export const isShippingMethodPrice = billingService => {
  return SHIPPING_METHOD_PRICE_SERVICE_TYPES.includes(billingService.service_type) && Boolean(billingService.shipping_method_id)
}

export const shippingMethodIdsIn = billingServices => {
  return [...new Set(billingServices.filter(isShippingMethodPrice).map(({ shipping_method_id }) => shipping_method_id))]
}

// A new shipping method price starts as a copy of the zone's default price, so ops only edit what
// was negotiated differently.
export const buildShippingMethodPrice = ({
  billingServices,
  shippingMethodId,
  category,
  currency,
  storeId,
  warehouseId,
  serviceType = SERVICE_TYPES.SERVICE_SHIPPING_LABEL,
}) => {
  const defaultRow = findOrCreateBillingService({ serviceType, serviceTypeCategory: category, billingServices })
  const priceConfiguration = billingServices.includes(defaultRow) ? defaultRow.price_configuration : EMPTY_PRICE_CONFIGURATION

  return {
    store_id: storeId,
    warehouse_id: warehouseId,
    service_type: serviceType,
    service_type_category: category,
    currency,
    price_configuration: cloneDeep(priceConfiguration),
    shipping_method_id: shippingMethodId,
  }
}

export const removeShippingMethodPrices = (billingServices, shippingMethodId, category = null, serviceType = null) => {
  return billingServices.filter(billingService => {
    const sameRows = isShippingMethodPrice(billingService) && billingService.shipping_method_id === shippingMethodId
    const sameZone = category === null || billingService.service_type_category === category
    const sameType = serviceType === null || billingService.service_type === serviceType
    return !(sameRows && sameZone && sameType)
  })
}

export const supportsBusinessType = channel => Boolean(channel) && !CHANNELS_WITHOUT_BUSINESS_TYPE.includes(channel)

export const isPriceEmpty = price => price === null || price === undefined || price === ''

const channelPriceKey = ({ channel, subchannel, business_type }) => {
  return `${channel}|${subchannel ?? ''}|${business_type ?? ''}`
}

// The API rejects a repeated (channel, subchannel, business_type), so flag every row
// after the first one holding the same combination.
export const findDuplicateIndexes = channelPrices => {
  const seen = new Set()
  const duplicates = new Set()

  ;(channelPrices || []).forEach((channelPrice, index) => {
    if (!channelPrice.channel) {
      return
    }

    const key = channelPriceKey(channelPrice)

    if (seen.has(key)) {
      duplicates.add(index)
    }
    seen.add(key)
  })

  return duplicates
}

// On a mixed D2C/B2B store the API requires a price for both business types of a channel.
export const findChannelsMissingBusinessTypePair = channelPrices => {
  const businessTypesByChannel = new Map()

  ;(channelPrices || []).forEach(({ channel, business_type }) => {
    if (!supportsBusinessType(channel)) {
      return
    }

    const businessTypes = businessTypesByChannel.get(channel) || new Set()
    if (business_type) {
      businessTypes.add(business_type)
    }
    businessTypesByChannel.set(channel, businessTypes)
  })

  const incomplete = new Set()

  businessTypesByChannel.forEach((businessTypes, channel) => {
    const hasBoth = businessTypes.has(CHANNEL_PRICE_BUSINESS_TYPES.D2C) && businessTypes.has(CHANNEL_PRICE_BUSINESS_TYPES.B2B)
    if (!hasBoth) {
      incomplete.add(channel)
    }
  })

  return incomplete
}

// A price of 0 is a valid charge, so only a missing channel or an empty price is invalid.
export const areChannelPricesValid = (channelPrices, requiresBusinessType = false) => {
  const rows = channelPrices || []

  const hasCompleteRows = rows.every(({ channel, price, business_type }) => {
    if (!channel || isPriceEmpty(price)) {
      return false
    }
    if (requiresBusinessType && supportsBusinessType(channel)) {
      return Boolean(business_type)
    }
    return !business_type
  })

  if (!hasCompleteRows || findDuplicateIndexes(rows).size > 0) {
    return false
  }

  return !requiresBusinessType || findChannelsMissingBusinessTypePair(rows).size === 0
}

// A configuration can be duplicated across stores with a different business model. Single-modality
// stores drop the business type (keeping one row per channel) and mixed stores get the missing half
// of each pair seeded with an empty price, so the operator has to fill it in.
export const adaptChannelPricesToModality = (channelPrices, requiresBusinessType) => {
  const rows = channelPrices || []

  if (!requiresBusinessType) {
    const seenChannels = new Set()

    return rows
      .filter(({ channel }) => {
        if (seenChannels.has(channel)) {
          return false
        }
        seenChannels.add(channel)
        return true
      })
      .map(({ business_type, ...channelPrice }) => channelPrice)
  }

  const businessTypesByChannel = new Map()

  rows.forEach(({ channel, business_type }) => {
    if (!supportsBusinessType(channel)) {
      return
    }
    const businessTypes = businessTypesByChannel.get(channel) || new Set()
    if (business_type) {
      businessTypes.add(business_type)
    }
    businessTypesByChannel.set(channel, businessTypes)
  })

  const adapted = []

  rows.forEach(({ business_type, ...channelPrice }) => {
    if (!supportsBusinessType(channelPrice.channel)) {
      adapted.push(channelPrice)
      return
    }

    const businessTypes = businessTypesByChannel.get(channelPrice.channel)
    let assignedBusinessType = business_type

    if (!assignedBusinessType) {
      assignedBusinessType = businessTypes.has(CHANNEL_PRICE_BUSINESS_TYPES.D2C) ? CHANNEL_PRICE_BUSINESS_TYPES.B2B : CHANNEL_PRICE_BUSINESS_TYPES.D2C
      businessTypes.add(assignedBusinessType)
    }

    adapted.push({ ...channelPrice, business_type: assignedBusinessType })

    Object.values(CHANNEL_PRICE_BUSINESS_TYPES)
      .filter(businessType => !businessTypes.has(businessType))
      .forEach(businessType => {
        businessTypes.add(businessType)
        adapted.push({ channel: channelPrice.channel, subchannel: channelPrice.subchannel ?? null, business_type: businessType, price: null })
      })
  })

  return adapted
}

export const normalizeChannelPrices = channelPrices => {
  if (!Array.isArray(channelPrices)) {
    return []
  }

  // business_type is omitted rather than nulled: a null no longer means "both".
  return channelPrices.map(({ channel, subchannel, business_type, price }) => ({
    channel: channel,
    subchannel: subchannel ?? null,
    ...(business_type ? { business_type: business_type } : {}),
    price: price,
  }))
}

// The API rejects a payload that would drop the channel prices of a previous
// configuration unless discard_channel_prices is sent explicitly.
export const requiresChannelPricesDiscard = error => {
  if (error?.response?.status !== 422) {
    return false
  }

  const data = error.response.data

  return data?.code === CHANNEL_PRICES_DISCARD_CODE || data?.error_code === CHANNEL_PRICES_DISCARD_CODE
}

const isMinimumFee = billingService => billingService.service_type === SERVICE_TYPES.SERVICE_MINIMUM_FEE

export const findMinimumFee = billingServices => (billingServices || []).find(isMinimumFee)

// The API rejects a price of 0, so unlike most prices a minimum fee has to be above it. An absent
// minimum is valid: the fee is optional.
export const isMinimumFeeValid = billingServices => {
  const minimumFee = findMinimumFee(billingServices)

  if (!minimumFee) {
    return true
  }

  const price = minimumFee.price_configuration?.[0]?.price

  return !isPriceEmpty(price) && Number(price) > 0
}

// The API rejects a minimum fee on anything but a monthly cutoff. No interval chosen yet is not a
// reason to drop it: the operator hasn't ruled monthly out.
export const dropMinimumFeeUnlessMonthly = (billingServices, cutoffInterval) => {
  if (!cutoffInterval || cutoffInterval === CONFIG_CUTOFF_INTERVALS.INTERVAL_MONTHLY) {
    return billingServices
  }

  return billingServices.filter(billingService => !isMinimumFee(billingService))
}

// Writes the store's single minimum fee row; the price stays as typed so a half-typed "1." survives.
export const upsertMinimumFee = (billingServices, { price, currency, storeId, warehouseId }) => {
  const minimumFee = findOrCreateBillingService({
    serviceType: SERVICE_TYPES.SERVICE_MINIMUM_FEE,
    billingServices,
    currency,
    storeId,
    warehouseId,
  })
  const priceConfiguration = [{ from: 0, to: null, price, price_mode: PRICE_MODES.PRICE_MODE_TOTAL, excess: false, range_exclusion: null }]

  return updateDefaultPriceConfiguration(billingServices, { ...minimumFee, currency }, priceConfiguration)
}

// A store's first configuration is seeded from another store's price list, whose minimum fee is a
// commercial term nobody chose for this store: swap it for the default of this currency.
export const applyMinimumFeeDefault = (billingServices, { currency, cutoffInterval, storeId, warehouseId }) => {
  const withoutCopiedMinimumFee = billingServices.filter(billingService => !isMinimumFee(billingService))
  const defaultMinimumFee = DEFAULT_MINIMUM_FEE_BY_CURRENCY[currency]

  if (!defaultMinimumFee || (cutoffInterval && cutoffInterval !== CONFIG_CUTOFF_INTERVALS.INTERVAL_MONTHLY)) {
    return withoutCopiedMinimumFee
  }

  return upsertMinimumFee(withoutCopiedMinimumFee, { price: defaultMinimumFee, currency, storeId, warehouseId })
}

export const getCurrency = warehouseCountry => {
  switch (warehouseCountry) {
    case 'MX':
      return 'MXN'
    case 'BR':
      return 'R$'
    case 'CL':
      return 'CLP'
    default:
      console.log(`No currency found for ${warehouseCountry}`)
  }
}

export const getShippingLabelCategories = warehouseCountry => {
  switch (warehouseCountry) {
    case 'MX':
      return NATIONAL_MX_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES
    case 'BR':
      return NATIONAL_BR_SHIPPING_LABELS_SERVICE_TYPE_CATEGORIES
    default:
      // Countries without per-zone national shipping tables (e.g. CL) get an
      // empty category set so the wizard renders with only the pickup service.
      return {}
  }
}

// Duplicating carries channel prices and shipping method prices over, including from another store,
// which the operator picks explicitly. Creating seeds from whichever store the warehouse returns
// first, and those are commercial terms nobody chose to copy.
export const mapLoadedBillingServices = (billingServices, { storeId, warehouseId, isDuplicating }) => {
  return billingServices
    .filter(billingService => isDuplicating || !billingService.shipping_method_id)
    .map(billingService => {
      const mappedService = {
        store_id: storeId,
        warehouse_id: warehouseId,
        service_type: billingService.service_type,
        price_configuration: billingService.price_configuration,
        currency: billingService.currency,
        service_type_category: billingService.service_type_category,
      }

      if (isDuplicating && TECH_FEE_SERVICE_TYPES.includes(billingService.service_type) && billingService.channel_prices?.length > 0) {
        mappedService.channel_prices = normalizeChannelPrices(billingService.channel_prices)
      }

      if (billingService.shipping_method_id) {
        mappedService.shipping_method_id = billingService.shipping_method_id
        mappedService.shipping_method_name = billingService.shipping_method_name
      }

      return mappedService
    })
}

export const toApiBillingServices = billingServices => {
  return billingServices.map(({ shipping_method_name: _shippingMethodName, ...billingService }) => billingService)
}

const BILLING_SERVICES_PAGE_SIZE = 800

export const fetchAllBillingServices = async (fetchPage, params) => {
  const billingServices = []
  let page = 1
  let lastPage = false

  while (!lastPage) {
    const result = await fetchPage({ ...params, per_page: BILLING_SERVICES_PAGE_SIZE, page })
    billingServices.push(...result.store_billing_services)
    lastPage = result.meta?.last_page !== false
    page += 1
  }

  return billingServices
}
